/**
 * Default token values
 * 
 * These are used when property data doesn't provide specific values.
 * Extracted from the Lofts at Gin Alley / Reside Living design system.
 */

import type { DesignTokens } from './types';

// ============================================================================
// Default Design Tokens
// ============================================================================

export const defaultTokens: DesignTokens = {
  color: {
    // Primary brand color - dark navy from Reside Living
    primary: '#0a1f2e',
    // Secondary brand color - gold/amber accent
    secondary: '#c9a961',
    // Accent for CTAs and highlights
    accent: '#c9a961',
    // Background colors
    background: '#ffffff',
    surface: '#f5f5f5',
    // Text colors
    text: '#1a1a1a',
    textMuted: '#6b7280',
    textOnPrimary: '#ffffff',
    textOnSecondary: '#0a1f2e',
    // Utility colors
    border: '#e5e7eb',
    error: '#dc2626',
    success: '#16a34a',
    warning: '#f59e0b',
    // Link colors — null by default so no CSS variable is emitted unless set
    hyperlink: null,
    linkHover: null,
    linkVisited: null,
    linkUnderline: null,
  },
  font: {
    // Font families extracted from the site
    heading: '"Didot Regular", "Playfair Display", Georgia, serif',
    body: '"TT Norms Pro Medium", "TTNorms-Regular", "Inter", sans-serif',
    accent: '"Rubik", sans-serif',
    mono: '"Fira Code", "Consolas", monospace',
  },
  fontSize: {
    xs: '0.75rem',      // 12px
    sm: '0.875rem',     // 14px
    base: '1rem',       // 16px
    lg: '1.125rem',     // 18px
    xl: '1.25rem',      // 20px
    '2xl': '1.5rem',    // 24px
    '3xl': '1.875rem',  // 30px
    '4xl': '2.25rem',   // 36px
    '5xl': '3rem',      // 48px
    '6xl': '3.75rem',   // 60px
  },
  fontWeight: {
    light: '300',
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
  },
  spacing: {
    xs: '0.25rem',      // 4px
    sm: '0.5rem',       // 8px
    md: '1rem',         // 16px
    lg: '1.5rem',       // 24px
    xl: '2rem',         // 32px
    '2xl': '3rem',      // 48px
    '3xl': '4rem',      // 64px
    '4xl': '6rem',      // 96px
  },
  radius: {
    none: '0',
    sm: '0.125rem',     // 2px
    md: '0.375rem',     // 6px
    lg: '0.5rem',       // 8px
    xl: '0.75rem',      // 12px
    full: '9999px',
  },
  shadow: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
  },
  breakpoint: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
  transition: {
    fast: '150ms ease-in-out',
    normal: '300ms ease-in-out',
    slow: '500ms ease-in-out',
  },
  zIndex: {
    dropdown: '1000',
    sticky: '1020',
    fixed: '1030',
    modal: '1040',
    popover: '1050',
    tooltip: '1060',
  },
};

// ============================================================================
// Reside Living Company Defaults
// ============================================================================

export const resideLivingDefaults: Partial<DesignTokens> = {
  color: {
    ...defaultTokens.color,
    primary: '#0a1f2e',
    secondary: '#c9a961',
    accent: '#c9a961',
  },
  font: {
    ...defaultTokens.font,
    heading: '"Didot Regular", Georgia, serif',
    body: '"TT Norms Pro Medium", sans-serif',
  },
};

// ============================================================================
// Theme Preset Factories
// ============================================================================

export type ThemePreset = 'default' | 'reside-living' | 'modern' | 'classic';

export function getPresetTokens(preset: ThemePreset): DesignTokens {
  switch (preset) {
    case 'reside-living':
      return {
        ...defaultTokens,
        color: { ...defaultTokens.color, ...resideLivingDefaults.color },
        font: { ...defaultTokens.font, ...resideLivingDefaults.font },
      };
    case 'modern':
      return {
        ...defaultTokens,
        font: {
          ...defaultTokens.font,
          heading: '"Inter", sans-serif',
          body: '"Inter", sans-serif',
        },
        radius: {
          ...defaultTokens.radius,
          md: '0.5rem',
          lg: '0.75rem',
        },
      };
    case 'classic':
      return {
        ...defaultTokens,
        font: {
          ...defaultTokens.font,
          heading: '"Playfair Display", Georgia, serif',
          body: '"Lora", Georgia, serif',
        },
      };
    default:
      return defaultTokens;
  }
}

